import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { HardHat, Mail, Phone, ArrowRight, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function VendorMagicLoginPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [channel, setChannel] = useState("email");
  const [sent, setSent] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [err, setErr] = useState(null);

  // Token in URL? Auto-redeem it.
  useEffect(() => {
    if (!token) return;
    (async () => {
      setRedeeming(true);
      try {
        const { data } = await axios.post(`${API}/vendor/magic-login/${token}`,
                                             null, { withCredentials: true });
        // Backend set the httpOnly access_token cookie — nothing to persist.
        toast.success("Signed in — welcome back");
        nav("/vendor/my-compliance");
      } catch (e) {
        setErr(e?.response?.data?.detail || "Link no longer valid.");
      }
      setRedeeming(false);
    })();
  }, [token, nav]);

  const requestLink = async () => {
    const key = channel === "email" ? email.trim().toLowerCase() : phone.trim();
    if (!key) return toast.error(`Enter your ${channel === "email" ? "email" : "phone"}`);
    try {
      const body = channel === "email" ? { email: key } : { phone: key };
      await axios.post(`${API}/vendor/magic-login-request`, body);
      setSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send. Try again.");
    }
  };

  if (redeeming) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-sm text-muted-foreground tracking-widest uppercase" data-testid="magic-redeeming">Signing you in…</div>
    </div>
  );

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="border-white/10 bg-card p-8 max-w-md text-center" data-testid="magic-error">
        <div className="text-lg font-semibold mb-2">Link unavailable</div>
        <div className="text-sm text-muted-foreground mb-4">{err}</div>
        <Button onClick={() => nav("/vendor-login-magic")} className="bg-orange-500 hover:bg-orange-600 rounded-full">Request a new link</Button>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="border-white/10 bg-card p-8 max-w-md w-full" data-testid="magic-request-page">
        <div className="flex items-center gap-3 mb-6">
          <HardHat className="h-6 w-6 text-orange-400" />
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest">Vendor portal</div>
            <div className="text-xl font-semibold">Sign in with email</div>
          </div>
        </div>
        {sent ? (
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-start gap-3" data-testid="magic-sent">
            <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-medium">Check your {channel === "sms" ? "phone" : "inbox"}</div>
              <div className="text-muted-foreground mt-1">
                If <b>{channel === "sms" ? phone : email}</b> is registered as a vendor, you'll receive a single-use link within a minute. It expires in 30 minutes.
              </div>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              Enter the email or phone you use for compliance notices. We'll send a one-tap sign-in link — no password required.
            </p>
            <div className="flex gap-1 mb-3 text-[11px]">
              {[["email", "Email"], ["sms", "SMS"]].map(([k, label]) => (
                <button key={k} onClick={() => setChannel(k)} data-testid={`channel-${k}`}
                  className={`px-3 py-1 rounded-full border ${channel === k ? "border-orange-500/40 text-orange-300 bg-orange-500/10" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}>{label}</button>
              ))}
            </div>
            {channel === "email" ? (
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <Input data-testid="magic-email" type="email" autoFocus placeholder="vendor@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && requestLink()}
                  className="pl-10 bg-white/[0.04] border-white/10" />
              </div>
            ) : (
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <Input data-testid="magic-phone" type="tel" autoFocus placeholder="+15551234567"
                  value={phone} onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && requestLink()}
                  className="pl-10 bg-white/[0.04] border-white/10" />
                <div className="text-[10px] text-zinc-500 mt-1">E.164 format (+ country code + number)</div>
              </div>
            )}
            <Button data-testid="magic-send" onClick={requestLink} className="w-full mt-3 bg-orange-500 hover:bg-orange-600 rounded-full">
              Send me a sign-in link <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
